import { useState } from "react";
import { useAction } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { PricingPlanId } from "@/lib/billing/plans";

export type CheckoutState = {
  /** Plan currently being checked out, null when idle */
  loadingPlan: PricingPlanId | null;
  /** Last checkout error, empty string when none */
  error: string;
  /** Clear the error message */
  clearError: () => void;
  /**
   * Initiate a Polar.sh checkout session for the given plan.
   *
   * - If the user already has an active subscription, Polar performs an in-place
   *   plan change (no hosted checkout). On success, the caller is notified via
   *   the `onPlanUpdated` callback.
   * - Otherwise the user is redirected to Polar's hosted checkout page.
   *   On return, Polar sends a webhook to `/api/webhooks/polar` which updates
   *   Convex with the new subscription state.
   *
   * @param planId  - One of "starter" | "plus" | "pro"
   * @param opts.withTrial       - Request a free trial (starter plan only)
   * @param opts.successRedirect - Relative path to redirect after checkout success
   * @param opts.onPlanUpdated   - Called when an in-place plan change succeeds
   */
  startCheckout: (
    planId: PricingPlanId,
    opts?: {
      withTrial?: boolean;
      successRedirect?: string;
      onPlanUpdated?: () => void;
    }
  ) => Promise<void>;
};

/**
 * useCheckout
 *
 * Centralised frontend trigger for Polar.sh checkout session initiation.
 * Wraps the `createSubscriptionCheckout` Convex action and handles:
 *   - Loading state per plan (prevents double-clicks / multi-click races)
 *   - In-place plan changes for existing subscribers (no hosted checkout)
 *   - Redirect to Polar hosted checkout for new subscriptions / trials
 *   - Error capture with a simple string message for inline UI display
 *
 * The success redirect URL and cancel URL are configured server-side in the
 * `createSubscriptionCheckout` action and point back to this webapp:
 *   success → /billing/success?checkout_id={CHECKOUT_ID}&plan={plan}
 *   cancel  → /pricing?canceled=true&plan={plan}
 *
 * @example
 * ```tsx
 * function SubscribeButton({ plan }: { plan: PricingPlanId }) {
 *   const { startCheckout, loadingPlan, error } = useCheckout();
 *   return (
 *     <>
 *       {error && <p className="text-red-600">{error}</p>}
 *       <button
 *         disabled={loadingPlan !== null}
 *         onClick={() => startCheckout(plan)}
 *       >
 *         {loadingPlan === plan ? "Loading…" : "Subscribe"}
 *       </button>
 *     </>
 *   );
 * }
 * ```
 */
export function useCheckout(): CheckoutState {
  const createCheckout = useAction(api.actions.createSubscriptionCheckout);
  const [loadingPlan, setLoadingPlan] = useState<PricingPlanId | null>(null);
  const [error, setError] = useState("");

  const startCheckout = async (
    planId: PricingPlanId,
    opts: {
      withTrial?: boolean;
      successRedirect?: string;
      onPlanUpdated?: () => void;
    } = {}
  ) => {
    setError("");
    setLoadingPlan(planId);
    try {
      const result = await createCheckout({
        plan: planId,
        withTrial: opts.withTrial,
        ...(opts.successRedirect ? { successRedirect: opts.successRedirect } : {}),
      });

      if (!result.ok) {
        throw new Error(result.error?.message || "Failed to start checkout.");
      }

      if (result.data?.updated) {
        // In-place plan change succeeded (existing subscriber switching tiers).
        // Convex subscription data updates reactively — no redirect needed.
        opts.onPlanUpdated?.();
        return;
      }

      if (result.data?.checkoutUrl) {
        window.location.href = result.data.checkoutUrl;
        return;
      }

      throw new Error("No checkout URL returned. Please try again.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to start checkout.");
    } finally {
      setLoadingPlan(null);
    }
  };

  return {
    loadingPlan,
    error,
    clearError: () => setError(""),
    startCheckout,
  };
}
